import React, { useRef } from 'react';
import { App, Button } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { addNotification } from '../../store/slices/notificationsSlice';
import { isChef } from '../../utils/roleHelpers';
import { OrderReadyEvent } from '../../services/notificationsApi';
import { useHubEvent } from '../../hooks/useHubEvent';
import { ORDER_READY_EVENT } from '../../services/realtimeHub';

/**
 * Componente sin UI: escucha el hub SignalR compartido (services/realtimeHub) y
 * dispara la alerta in-app (sonido + vibración + banner persistente) cuando cocina
 * marca una orden lista.
 *
 * El evento llega a todos los dispositivos del restaurante; aquí se filtra:
 * suena solo para el mesero dueño de la orden, o para todos si la orden no
 * tiene mesero asignado. El Chef no escucha (él la marcó lista en el KDS).
 */
const OrderReadyListener: React.FC = () => {
  const { notification } = App.useApp();
  const dispatch = useAppDispatch();
  const { user, accessToken } = useAppSelector((state) => state.auth);

  const audioContextRef = useRef<AudioContext | null>(null);
  const userIdRef = useRef<string | undefined>(user?.id);
  userIdRef.current = user?.id;

  const enabled = !!user && !!accessToken && !isChef(user.role);

  // Mismo patrón de beep sintetizado que el KDS (sin archivos de audio)
  const playReadySound = () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const ctx = audioContextRef.current;

      const playBeep = (startTime: number, frequency: number, duration: number) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      // Melodía ascendente doble para distinguirse de la alarma del KDS
      const now = ctx.currentTime;
      playBeep(now, 700, 0.15);
      playBeep(now + 0.2, 900, 0.15);
      playBeep(now + 0.4, 1100, 0.25);
      playBeep(now + 0.9, 700, 0.15);
      playBeep(now + 1.1, 900, 0.15);
      playBeep(now + 1.3, 1100, 0.25);
    } catch (err) {
      // silently ignore
    }
  };

  useHubEvent(
    ORDER_READY_EVENT,
    (event: OrderReadyEvent) => {
      const currentUserId = userIdRef.current;
      // Orden con mesero asignado → solo su dueño; sin mesero → todos
      if (event.waiterId && event.waiterId !== currentUserId) {
        return;
      }

      // En llevar/domicilio tableNumber trae el nombre del cliente (o vacío), no una mesa
      const customer = event.tableNumber ? ` (${event.tableNumber})` : '';
      const title =
        event.orderType === 'TakeOut'
          ? `Para llevar${customer} — orden #${event.orderNumber} lista`
          : event.orderType === 'Delivery'
            ? `Domicilio${customer} — orden #${event.orderNumber} lista`
            : `Mesa ${event.tableNumber} — orden #${event.orderNumber} lista`;
      const description = event.itemsSummary || 'Pasa a cocina a recogerla';

      playReadySound();
      if ('vibrate' in navigator) {
        navigator.vibrate([300, 100, 300, 100, 300]);
      }

      const key = `order-ready-${event.kitchenOrderId}`;
      notification.success({
        key,
        message: title,
        description,
        placement: 'top',
        duration: 0, // persistente hasta que el mesero la atienda
        btn: (
          <Button type="primary" size="small" icon={<CheckOutlined />} onClick={() => notification.destroy(key)}>
            Voy por ella
          </Button>
        ),
      });

      // También al feed de la campanita del header
      dispatch(
        addNotification({
          type: 'success',
          title,
          message: description,
          actionUrl: '/tables',
        })
      );
    },
    enabled
  );

  return null;
};

export default OrderReadyListener;
